import React, { useCallback, useEffect, useState } from "react";
import type { GetServerSideProps } from "next";
import Link from "next/link";
import { useRouter } from "next/router";
import SiteShell from "@/components/SiteShell";
import JsonLd from "@/components/JsonLd";
import {
  FOUNDING_MEMBER_AMOUNT,
  FOUNDING_MEMBER_CURRENCY,
  getFoundingMemberAvailability,
  type FoundingMemberAvailability,
} from "@/lib/foundingOffer";
import { SITE_URL, faqLd, foundingOfferLd } from "@/lib/structuredData";

type OfferProps = FoundingMemberAvailability & {
  amount: number;
  currency: string;
};

export const getServerSideProps: GetServerSideProps<OfferProps> = async () => {
  const availability = await getFoundingMemberAvailability();
  return {
    props: {
      ...availability,
      amount: FOUNDING_MEMBER_AMOUNT,
      currency: FOUNDING_MEMBER_CURRENCY,
    },
  };
};

/**
 * One source for the questions: the page renders these and the FAQPage schema
 * is built from the same array, so an assistant quoting the schema quotes what
 * a buyer actually reads.
 */
function faqEntries(price: string): { q: string; a: string }[] {
  return [
    { q: "Is this a subscription?", a: `No. Stripe lists this ${price} price as a one-time payment.` },
    {
      q: "How many Founding Member spots are there?",
      a: "100 total. A spot is claimed after a successful payment, not when someone merely opens checkout.",
    },
    {
      q: "What happens when all 100 are claimed?",
      a: "The checkout closes and the Founding Member offer is no longer available.",
    },
    {
      q: "How can I get a Refund?",
      a: "There will be no refunds on this offer. I want to work with committed business owners who are ALL IN. If you want in, LFG. If you're scared- This isn't for you!",
    },
    {
      q: "Do I have to pay for SMS / Email providers?",
      a: "Yes. Frazzle only provides the automation mechanisms- The actual sending of emails and texts requires a paid subscription to 3rd party providers that Frazzle has nothing to do with. That being said, We don't take a cut like GHL does- so no lame middle-man theft.",
    },
    {
      q: "How do I actually get my access?",
      a: "Create your FrazzleCRM account with the same email you paid with, and your workspace unlocks itself the first time you open it — no code to redeem. If you already have an account on that email it unlocks straight away. Either way you get an email confirming it is live.",
    },
    {
      q: "What does Founding Member access include?",
      a: "Founder-level access on your workspace: unlimited contacts, flows, maps, members and map nodes, plus AI access over MCP. It applies to the first workspace you own; extra workspaces you create later run on their own plan.",
    },
    {
      q: "Where do I go to get support?",
      a: "Founding Members will be granted access to a closed Discord community. If you need help or run into trouble getting up and running, I will be happy to assist.",
    },
    {
      q: "Can you help me set up Automations for my Business?",
      a: "Sure, but I would charge a consulting fee for such a service. Inquire about that in the Discord later.",
    },
    {
      q: "Where is payment handled?",
      a: "Stripe hosts the checkout. Frazzle never receives or stores your card number.",
    },
  ];
}

function formatPrice(amount: number, currency: string) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    maximumFractionDigits: 0,
  }).format(amount / 100);
}

export default function FoundingMembers(initial: OfferProps) {
  const router = useRouter();
  const [availability, setAvailability] = useState(initial);
  const [checkingOut, setCheckingOut] = useState(false);
  const [error, setError] = useState("");
  const price = formatPrice(availability.amount, availability.currency);
  const claimedPercent = Math.round((availability.sold / availability.total) * 100);
  const faq = faqEntries(price);

  const refreshAvailability = useCallback(() => {
    fetch("/api/founding-members/availability")
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (typeof data?.remaining === "number") setAvailability(data);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const timer = window.setInterval(refreshAvailability, 30_000);
    return () => window.clearInterval(timer);
  }, [refreshAvailability]);

  async function beginCheckout() {
    setCheckingOut(true);
    setError("");
    try {
      const response = await fetch("/api/founding-members/checkout", { method: "POST" });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.url) throw new Error(data.error || "Checkout is unavailable.");
      window.location.assign(data.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Checkout is unavailable.");
      setCheckingOut(false);
      refreshAvailability();
    }
  }

  const checkoutState = router.query.checkout;

  return (
    <SiteShell
      title="Never pay monthly for a CRM again | Frazzle Founding Member"
      description="Claim one of 100 Frazzle Founding Member spots. One $397 payment, with no recurring Frazzle CRM subscription."
      canonical={`${SITE_URL}/founding-members`}
      ogTitle="Never pay monthly for a CRM again"
    >
      {/* Product/Offer carries the price, the seller and the live spot count;
          FAQPage carries the same eight answers the page renders. Both are
          built from server-rendered values, so neither can drift from what a
          buyer sees. */}
      <JsonLd
        data={foundingOfferLd({
          price: availability.amount / 100,
          currency: availability.currency,
          total: availability.total,
          remaining: availability.remaining,
          soldOut: availability.soldOut,
        })}
      />
      <JsonLd data={faqLd(faq)} />
      <main className="overflow-hidden bg-slate-950 text-white">
        <section className="relative isolate">
          <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_15%_20%,rgba(99,102,241,0.28),transparent_32%),radial-gradient(circle_at_85%_10%,rgba(245,158,11,0.18),transparent_28%),linear-gradient(to_bottom,#020617,#0f172a)]" />
          <div className="mx-auto grid max-w-6xl items-center gap-12 px-6 pb-20 pt-16 lg:grid-cols-[1.15fr_0.85fr] lg:pb-24 lg:pt-24">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-amber-300/30 bg-amber-300/10 px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.16em] text-amber-300">
                Frazzle Founding Member
              </div>
              <h1 className="mt-6 max-w-3xl text-[44px] font-black leading-[1.02] tracking-[-0.035em] text-white sm:text-[62px]">
                Never pay monthly for a CRM again.
              </h1>
              <p className="mt-6 max-w-2xl text-[17px] leading-relaxed text-slate-300 sm:text-[19px]">
                Pay once for Frazzle Founding Member access. Manage your contacts,
                Map your Funnels and Automate your business without spending
                another dime.
              </p>

              <div className="mt-8 grid max-w-2xl gap-3 sm:grid-cols-3">
                {[
                  ["One Payment", "Not a subscription"],
                  ["No Premium Actions", "All features included"],
                  ["No Middle Man", "BYO Email/Text providers"],
                ].map(([title, body]) => (
                  <div key={title} className="rounded-2xl border border-white/10 bg-white/[0.045] p-4">
                    <div className="text-[14px] font-extrabold text-white">{title}</div>
                    <div className="mt-1 text-[12.5px] text-slate-400">{body}</div>
                  </div>
                ))}
              </div>
            </div>

            <aside className="rounded-[28px] border border-white/15 bg-white p-6 text-slate-900 shadow-2xl shadow-indigo-950/60 sm:p-8">
              {checkoutState === "success" && (
                <div className="mb-5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-800">
                  <p className="text-[13.5px] font-bold">
                    Payment received. Welcome to the founding group.
                  </p>
                  <p className="mt-1.5 text-[12.5px] leading-relaxed">
                    One step left: create your account with{" "}
                    <strong>the same email you just paid with</strong> — that is
                    how your payment finds you. Your workspace unlocks by itself
                    the first time you open it. Already have an account on that
                    email? It is unlocking now; just sign in.
                  </p>
                  <a
                    href="https://app.frazzlecrm.com/signup"
                    className="mt-3 inline-flex min-h-[44px] items-center rounded-lg bg-emerald-700 px-4 py-2.5 text-[13px] font-extrabold text-white hover:bg-emerald-600"
                  >
                    Create your account
                  </a>
                </div>
              )}
              {checkoutState === "cancelled" && (
                <div className="mb-5 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-[13px] text-slate-600">
                  No charge was made. Your spot is not claimed until checkout is complete.
                </div>
              )}

              <div className="flex items-start justify-between gap-5">
                <div>
                  <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-indigo-600">
                    Founding Member
                  </p>
                  <p className="mt-2 text-[13.5px] leading-relaxed text-slate-500">
                    The last time you ever pay for a CRM.
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-[38px] font-black tracking-tight text-slate-950">{price}</div>
                  <div className="text-[12px] font-semibold text-slate-400">one time</div>
                </div>
              </div>

              <div className="mt-7 rounded-2xl border border-amber-200 bg-amber-50 p-4">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-[13px] font-bold text-amber-950">Founding spots remaining</span>
                  <span className="text-[25px] font-black text-amber-950" aria-live="polite">
                    {availability.remaining}
                    <span className="text-[13px] font-bold text-amber-700"> / {availability.total}</span>
                  </span>
                </div>
                <div className="mt-3 h-2 overflow-hidden rounded-full bg-amber-200/70">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-amber-500 to-orange-500 transition-[width] duration-500"
                    style={{ width: `${claimedPercent}%` }}
                  />
                </div>
                <p className="mt-2 text-[11.5px] text-amber-800">
                  Once this number hits 0, It&apos;s over.
                </p>
              </div>

              <ul className="mt-6 space-y-3 text-[13.5px] text-slate-700">
                {[
                  "Buy once, Access for Life",
                  "No Hidden fees or Gotchas",
                  "All the features you want from a CRM- And some you'll never want to live without.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2.5">
                    <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-emerald-100 text-[11px] font-black text-emerald-700">✓</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <button
                type="button"
                onClick={beginCheckout}
                disabled={availability.soldOut || checkingOut}
                className="mt-7 w-full rounded-xl bg-indigo-600 px-5 py-3.5 text-[15px] font-extrabold text-white shadow-lg shadow-indigo-200 transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none"
              >
                {availability.soldOut
                  ? "All 100 spots are claimed"
                  : checkingOut
                    ? "Opening secure checkout…"
                    : `Claim my spot for ${price}`}
              </button>
              {error && <p className="mt-3 text-center text-[12.5px] font-semibold text-red-600">{error}</p>}
              <p className="mt-3 text-center text-[11.5px] text-slate-400">
                No Refunds. Either you&apos;re In or you&apos;re out!
              </p>
            </aside>
          </div>
        </section>

        <section className="border-y border-white/10 bg-white/[0.035]">
          <div className="mx-auto max-w-5xl px-6 py-16 text-center">
            <p className="text-[12px] font-extrabold uppercase tracking-[0.15em] text-violet-300">
              Built for Winners
            </p>
            <h2 className="mx-auto mt-4 max-w-3xl text-[30px] font-black tracking-tight text-white sm:text-[38px]">
              Built by a 7-figure Technical Founder
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-[16px] leading-relaxed text-slate-300">
              &ldquo;I built Frazzle because i&apos;m sick of being nickled and dimed by
              GHL- And GHL doesn&apos;t even have basic quality of life features that
              actually help me scale my business.&rdquo;
              <span className="mt-4 block text-[13px] font-bold text-slate-400">
                Jason Casey - Technical Founder of Starter School
              </span>
            </p>
          </div>
        </section>

        <section className="mx-auto max-w-5xl px-6 py-20">
          <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr]">
            <div>
              <p className="text-[12px] font-extrabold uppercase tracking-[0.15em] text-amber-300">Questions</p>
              <h2 className="mt-3 text-[32px] font-black tracking-tight text-white">No fine-print</h2>
              <p className="mt-4 text-[14px] leading-relaxed text-slate-400">
                1 price, lifetime access- simple as that.
              </p>
            </div>
            <div className="divide-y divide-white/10 border-y border-white/10">
              {faq.map(({ q, a }) => (
                <div key={q} className="py-5">
                  <h3 className="text-[15px] font-extrabold text-white">{q}</h3>
                  <p className="mt-2 text-[14px] leading-relaxed text-slate-400">{a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Not everyone lands here ready to pay $397. Sending them onward
            beats a dead end. */}
        <section className="border-t border-white/10">
          <div className="mx-auto max-w-5xl px-6 py-12 text-center">
            <p className="text-[14px] leading-relaxed text-slate-400">
              Not ready? Look at the{" "}
              <Link href="/pricing" className="font-semibold text-indigo-300 hover:text-indigo-200">
                monthly plans
              </Link>
              , start on the{" "}
              <Link href="/templates" className="font-semibold text-indigo-300 hover:text-indigo-200">
                free tier with a template
              </Link>
              , or build a funnel map without signing up at all with the{" "}
              <Link href="/tools/funnel-generator" className="font-semibold text-indigo-300 hover:text-indigo-200">
                funnel generator
              </Link>
              .
            </p>
          </div>
        </section>
      </main>
    </SiteShell>
  );
}
